/**
 * Shared AI insight surface and legacy card-result contracts.
 *
 * @remarks
 * This package owns the stable surface identifiers and lightweight payload
 * types shared by the customer app and API. Prompt generation, model linting,
 * entitlements, state persistence, and UI rendering stay in their existing
 * surface/API modules.
 *
 * The generic overview surface is part of the API/customer-app contract for the
 * Home AI Insights panel. Admin's legacy bridge remains report-surface-only
 * because that was its previous local type.
 *
 * @see docs/HelloCashflowApp.md
 * @see docs/HelloCashflowNodeJs.md
 * @see docs/guides/PLAIN_LANGUAGE.md
 */
export type ReportAiInsightSurfaceId =
  | "reports.profit_loss"
  | "reports.cashflow"
  | "reports.balance_sheet"
  | "reports.sales_tax"
  | "reports.budget_vs_actual";

export type GenericAiInsightSurfaceId = "generic.overview";

export type AiInsightSurfaceId =
  ReportAiInsightSurfaceId | GenericAiInsightSurfaceId;

export const REPORT_AI_INSIGHT_SURFACE_IDS = [
  "reports.profit_loss",
  "reports.cashflow",
  "reports.balance_sheet",
  "reports.sales_tax",
  "reports.budget_vs_actual",
] as const satisfies readonly ReportAiInsightSurfaceId[];

export const AI_INSIGHT_SURFACE_IDS = [
  ...REPORT_AI_INSIGHT_SURFACE_IDS,
  "generic.overview",
] as const satisfies readonly AiInsightSurfaceId[];

export type AiInsightSeverity = "info" | "positive" | "warning";

export interface AiInsightCard {
  title: string;
  prompt: string;
  breakdown?: string[];
  severity: AiInsightSeverity;
  category: string;
}

export interface AiInsightResult {
  surfaceId: AiInsightSurfaceId;
  periodEndMonthKey: string;
  generatedAtIso: string;
  fromCache: boolean;
  cards: AiInsightCard[];
}
